import {
  DragDropContext,
  Draggable,
  type DraggableProvided,
  type DropResult,
  Droppable,
} from 'react-beautiful-dnd';
import { FieldArray } from 'react-final-form-arrays';

import { ExpertRequestFieldPermission, QueryResponseType } from '@/__generated__/graphql';
import Button from '@/components/Button';
import { type FieldGroupName, queryRowPerm } from '@/expertrequest/utils/fieldPermissions';

import { HiddenVettingSection, VettingQueryCard } from './VettingQueries';

interface VettingQueryValue {
  id?: string;
  query?: string;
  required?: boolean;
  response_type?: QueryResponseType;
  hidden?: boolean;
}

interface ExpertQueriesProps {
  name: 'questions' | 'qualifications';
  /** Form-level interactivity (questionsInteractive / qualificationsInteractive). */
  interactive: boolean;
  isViewerExpert: boolean;
  fieldPermissions?: readonly ExpertRequestFieldPermission[] | null;
  onToggleHidden: (query: VettingQueryValue) => void;
}

/**
 * Vetting question / qualification list (ENG-5292). Renders the redesigned
 * carded rows: visible items drag-reorder inside a single DnD list, hidden items
 * collapse into a "Hidden from experts" section. Per-row editability comes from
 * the `field_permissions` matrix; hide/show go through the parent's mutation.
 */
export default function ExpertQueries({
  name,
  interactive,
  isViewerExpert,
  fieldPermissions,
  onToggleHidden,
}: ExpertQueriesProps): JSX.Element {
  const group = name as FieldGroupName;
  const noun = name === 'questions' ? 'question' : 'qualification';
  const readOnlyAll = isViewerExpert || !interactive;

  return (
    <FieldArray name={name}>
      {({ fields }) => {
        const values: VettingQueryValue[] = fields.value ?? [];
        const visible: { value: VettingQueryValue; index: number }[] = [];
        const hidden: { value: VettingQueryValue; index: number }[] = [];
        values.forEach((value, index) => {
          (value.hidden ? hidden : visible).push({ value, index });
        });

        const handleDragEnd = (result: DropResult): void => {
          if (!result.destination) return;
          const from = visible[result.source.index]?.index;
          const to = visible[result.destination.index]?.index;
          if (from === undefined || to === undefined) return;
          fields.move(from, to);
        };

        return (
          <>
            <DragDropContext onDragEnd={handleDragEnd}>
              <Droppable droppableId={`vetting-${name}`}>
                {(dropProvided) => (
                  <div
                    ref={dropProvided.innerRef}
                    {...dropProvided.droppableProps}
                    className="flex flex-col gap-14"
                  >
                    {visible.map(({ value, index }, visibleIndex) => {
                      const perm = readOnlyAll
                        ? 'read'
                        : queryRowPerm({ permissions: fieldPermissions, group, hasId: !!value.id });
                      const draggableId = value.id ?? `${name}-new-${index}`;
                      return (
                        <Draggable
                          key={draggableId}
                          draggableId={draggableId}
                          index={visibleIndex}
                          isDragDisabled={perm === 'read'}
                        >
                          {(dragProvided: DraggableProvided) => (
                            <div ref={dragProvided.innerRef} {...dragProvided.draggableProps}>
                              <VettingQueryCard
                                id={draggableId}
                                noun={noun}
                                perm={perm}
                                isPersisted={!!value.id}
                                text={value.query ?? ''}
                                required={!!value.required}
                                responseType={value.response_type ?? QueryResponseType.FreeForm}
                                dragHandleProps={
                                  perm === 'read' ? null : dragProvided.dragHandleProps
                                }
                                onText={(query) => fields.update(index, { ...value, query })}
                                onRequired={(required) =>
                                  fields.update(index, { ...value, required })
                                }
                                onResponseType={(responseType) =>
                                  fields.update(index, { ...value, response_type: responseType })
                                }
                                onHide={() => onToggleHidden(value)}
                                onDelete={() => fields.remove(index)}
                              />
                            </div>
                          )}
                        </Draggable>
                      );
                    })}
                    {dropProvided.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>

            {!readOnlyAll && (
              <Button
                color="white"
                size="normal"
                sx={{
                  marginTop: '16px',
                  minWidth: '185px',
                  minHeight: '32px',
                  padding: '6px 14px',
                  fontSize: '13px',
                }}
                onClick={() =>
                  fields.push({
                    required: true,
                    response_type: QueryResponseType.YesNo,
                    hidden: false,
                  })
                }
              >
                Add {noun}
              </Button>
            )}

            <HiddenVettingSection
              noun={noun}
              canShow={!readOnlyAll}
              items={hidden
                .filter((h) => h.value.id)
                .map((h) => ({ id: h.value.id as string, text: h.value.query ?? '' }))}
              onShow={(id) => {
                const match = hidden.find((h) => h.value.id === id);
                if (match) onToggleHidden(match.value);
              }}
            />
          </>
        );
      }}
    </FieldArray>
  );
}
